<template>
  <div class="sb-side-menu">
    <template v-if="isLoading">
      <SbLoading type="placeholder" class="sb-side-menu__loading">
        <SbLoadingPlaceholder width="120px" height="24px" />
        <SbLoadingPlaceholder width="120px" height="24px" />
        <SbLoadingPlaceholder width="120px" height="24px" />
        <SbLoadingPlaceholder width="120px" height="24px" />
      </SbLoading>
    </template>
    <template v-else>
      <nav ref="sideMenuRef" class="sb-side-menu__wrapper">
        <PrimeTabMenu
          v-if="!hasCategories"
          v-bind="props"
          :pt="computedPassthrough"
          unstyled
          @tab-change="handleTabChange"
        >
          <template v-if="$slots.item" #item="{ item, props }">
            <slot name="item" :item="item" :props="props" />
          </template>
        </PrimeTabMenu>
        <section
          v-else
          v-for="(item, index) in model"
          :key="`category-${index}`"
          :aria-labelledby="`category-id-${index}`"
        >
          <h2 :id="`category-id-${index}`" class="sb-side-menu__category-title">
            {{ item.label }}
          </h2>
          <PrimeTabMenu
            :key="`tabmenu-${item.label}-${index}`"
            v-bind="props"
            :active-index="activeStates?.[index] ?? -1"
            :model="item.items"
            :pt="computedPassthrough"
            unstyled
            @tab-change="(e) => handleTabChange(e, index)"
          >
            <template v-if="$slots.item" #item="{ item, props }">
              <slot name="item" :item="item" :props="props" />
            </template>
          </PrimeTabMenu>
        </section>
      </nav>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { SbLoading, SbLoadingPlaceholder } from '..'
import PrimeTabMenu, {
  type TabMenuChangeEvent,
  type TabMenuPassThroughOptions,
} from 'primevue/tabmenu'
import type { MenuItem } from 'primevue/menuitem'
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'

type CategoryItem = MenuItem & {
  items?: MenuItem[]
}

type SideMenuProps = {
  model?: MenuItem[] | CategoryItem[]
  activeIndex?: number
  ariaLabelledby?: string | undefined
  ariaLabel?: string | undefined
  scrollable?: boolean
  isLoading?: boolean
  pt?: TabMenuPassThroughOptions
  locale?: string
  hasCategories?: boolean
}

type SideMenuEmits = {
  'update:activeIndex': [value: number]
  'tab-change': [item: MenuItem]
}

const props = withDefaults(defineProps<SideMenuProps>(), {
  model: () => [],
  activeIndex: 0,
  ariaLabelledby: undefined,
  ariaLabel: undefined,
  scrollable: true,
  isLoading: false,
  pt: () => ({}),
  locale: 'en',
  hasCategories: false,
})

const emits = defineEmits<SideMenuEmits>()

const categoryStructure = computed(() => {
  if (!props.hasCategories) return undefined

  let globalIndex = 0
  return (props.model as CategoryItem[]).map((category: CategoryItem) => {
    const startIndex = globalIndex
    const itemCount = category.items?.length || 0
    globalIndex += itemCount
    return { startIndex, itemCount }
  })
})

const activeStates = computed(() => {
  if (!props.hasCategories || !categoryStructure.value) return undefined

  return categoryStructure.value.map(({ startIndex, itemCount }) => {
    const endIndex = startIndex + itemCount - 1
    const isActive =
      props.activeIndex >= startIndex && props.activeIndex <= endIndex
    return isActive ? props.activeIndex - startIndex : -1
  })
})

const computedPassthrough = computed(() => ({
  root: 'sb-side-menu__container',
  menu: 'sb-side-menu__menu',
  menuitem: 'sb-side-menu__menu-item',
  action: (options: {
    context?: {
      disabled?: boolean
      item?: { disabled?: boolean; id?: string }
    }
    item?: { disabled?: boolean; id?: string }
    disabled?: boolean
  }) => {
    const isDisabled =
      options.context?.disabled ||
      options.context?.item?.disabled ||
      options.item?.disabled ||
      options.disabled
    return {
      class: 'sb-side-menu__action',
      tabindex: '0',
      disabled: false,
      id: options.context?.item?.id,
      'aria-disabled': isDisabled ? 'true' : undefined,
    }
  },
  icon: 'sb-side-menu__icon',
  label: 'sb-side-menu__label',
  hooks: 'sb-side-menu__hooks',
  ...props.pt,
}))

const handleTabChange = (
  e: TabMenuChangeEvent,
  categoryIndex?: number,
): void => {
  if (
    props.hasCategories &&
    categoryIndex !== undefined &&
    categoryStructure.value
  ) {
    const categoryData = categoryStructure.value[categoryIndex]
    const category = (props.model as CategoryItem[])[categoryIndex]
    const item = category?.items?.[e.index]
    if (categoryData && item) {
      const globalIndex = categoryData.startIndex + e.index
      emits('tab-change', item)
      emits('update:activeIndex', globalIndex)
    }
  } else {
    const item = props.model[e.index]
    if (item) {
      emits('tab-change', item)
      emits('update:activeIndex', e.index)
    }
  }
}

const findCategoryForFocusIndex = (focusIndex: number) => {
  if (!categoryStructure.value) return undefined

  let remaining = focusIndex
  for (
    let categoryIndex = 0;
    categoryIndex < categoryStructure.value.length;
    categoryIndex++
  ) {
    const category = categoryStructure.value[categoryIndex]
    if (category && remaining < category.itemCount) {
      return {
        categoryIndex,
        itemIndex: remaining,
        globalIndex: category.startIndex + remaining,
      }
    }
    if (category) remaining -= category.itemCount
  }
  return undefined
}

const sideMenuRef = ref<HTMLElement>()

const handleKeydown = (event: KeyboardEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.sb-side-menu__action')) return

  if (event.key === 'Tab') {
    event.stopImmediatePropagation()
    return
  }

  if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
    event.preventDefault()
    event.stopImmediatePropagation()
    return
  }

  if (event.key === 'Enter' && props.hasCategories) {
    event.preventDefault()

    if (target.getAttribute('aria-disabled') === 'true') {
      return
    }

    const allActions = Array.from(
      sideMenuRef.value?.querySelectorAll(
        '.sb-side-menu__action:not([aria-disabled="true"])',
      ) || [],
    ) as HTMLElement[]

    const currentIndex = allActions.indexOf(target)
    const categoryInfo = findCategoryForFocusIndex(currentIndex)

    if (categoryInfo) {
      emits('update:activeIndex', categoryInfo.globalIndex)
      handleTabChange(
        { index: categoryInfo.itemIndex } as TabMenuChangeEvent,
        categoryInfo.categoryIndex,
      )
    }
  }
}

onMounted(() => {
  sideMenuRef.value?.addEventListener('keydown', handleKeydown, {
    capture: true,
  })
})

onBeforeUnmount(() => {
  sideMenuRef.value?.removeEventListener('keydown', handleKeydown, {
    capture: true,
  })
})
</script>

<style lang="scss" src="./side-menu.scss" />
